import { useCallback } from 'react';
import { useAppDispatch } from '../store';
import {
  completeActivity,
  updateActivityProgress,
  forceSyncAll,
} from '../store/slices/journeySlice';
import type { CompletionInput } from '@coffeeplexity/shared';
import { logger } from '../utils/logger';

/**
 * Hook for offline-aware journey operations
 *
 * Provides convenient methods for journey operations that work
 * both online and offline with optimistic updates.
 */
export const useOfflineJourney = () => {
  const dispatch = useAppDispatch();

  /**
   * Complete an activity with optimistic update
   */
  const completeActivityOffline = useCallback(
    async (activityId: string, payload: CompletionInput, userId?: string) => {
      try {
        const params: any = {
          activityId,
          payload,
        };
        if (userId) {
          params.userId = userId;
        }
        await dispatch(completeActivity(params)).unwrap();
        return true;
      } catch (error) {
        logger.error('Failed to complete activity:', error);
        return false;
      }
    },
    [dispatch]
  );

  /**
   * Update activity progress with optimistic update
   */
  const updateProgressOffline = useCallback(
    async (activityId: string, progressPercentage: number, userId?: string) => {
      try {
        const params: any = {
          activityId,
          progressPercentage,
        };
        if (userId) {
          params.userId = userId;
        }
        await dispatch(updateActivityProgress(params)).unwrap();
        return true;
      } catch (error) {
        logger.error('Failed to update progress:', error);
        return false;
      }
    },
    [dispatch]
  );

  /**
   * Force sync all pending operations
   */
  const syncAll = useCallback(async () => {
    try {
      await dispatch(forceSyncAll()).unwrap();
      return true;
    } catch (error) {
      logger.error('Failed to sync:', error);
      return false;
    }
  }, [dispatch]);

  return {
    completeActivityOffline,
    updateProgressOffline,
    syncAll,
  };
};
